import { supabase } from '../lib/supabase';

/**
 * Prestige System Service
 * Handles prestige mechanics - resetting level at max for exclusive badges and elite content
 */

export const prestigeService = {
  /**
   * Check if user can prestige (level 50+)
   * @returns {Promise<Object>} {canPrestige, currentLevel, currentXp, prestigeLevel}
   */
  async checkPrestigeEligibility() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('level, xp_points, prestige_level, can_prestige, lifetime_xp')
        .eq('id', user.id)
        .single();

      return {
        canPrestige: profile?.can_prestige || profile?.level >= 75,
        currentLevel: profile?.level || 1,
        currentXp: profile?.xp_points || 0,
        lifetimeXp: profile?.lifetime_xp || 0,
        prestigeLevel: profile?.prestige_level || 0
      };
    } catch (error) {
      console.error('Error checking prestige eligibility:', error);
      throw error;
    }
  },

  /**
   * Execute prestige - reset level, gain badge, unlock drills
   * @returns {Promise<Object>} Result with new prestige level, badge, drills unlocked
   */
  async executePrestige() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call database function to handle prestige
      const { data, error } = await supabase.rpc('execute_prestige', {
        user_uuid: user.id
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error executing prestige:', error);
      throw error;
    }
  },

  /**
   * Get prestige history for current user
   * @returns {Promise<Array>} List of prestige events
   */
  async getPrestigeHistory() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('prestige_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching prestige history:', error);
      return [];
    }
  },

  /**
   * Get prestige badges for current user
   * @returns {Promise<Array>} List of earned prestige badges
   */
  async getPrestigeBadges() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('prestige_badges, prestige_level')
        .eq('id', user.id)
        .single();

      return {
        badges: profile?.prestige_badges || [],
        prestigeLevel: profile?.prestige_level || 0
      };
    } catch (error) {
      console.error('Error fetching prestige badges:', error);
      return { badges: [], prestigeLevel: 0 };
    }
  },

  /**
   * Get prestige drills available to user based on prestige level
   * @returns {Promise<Array>} List of unlocked prestige drills
   */
  async getPrestigeDrills() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's prestige level
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('prestige_level')
        .eq('id', user.id)
        .single();

      const prestigeLevel = profile?.prestige_level || 0;

      // Get drills unlocked at this prestige level or below
      const { data, error } = await supabase
        .from('prestige_drills')
        .select('*')
        .lte('prestige_level_required', prestigeLevel)
        .eq('is_active', true)
        .order('prestige_level_required', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Convert to camelCase
      return (data || []).map(drill => ({
        id: drill.id,
        prestigeLevelRequired: drill.prestige_level_required,
        title: drill.title,
        promptText: drill.prompt_text,
        difficultyLevel: drill.difficulty_level,
        industry: drill.industry,
        idealAnswer: drill.ideal_answer,
        coachingNotes: drill.coaching_notes,
        badgeIcon: drill.badge_icon,
        badgeColor: drill.badge_color,
        isActive: drill.is_active,
        createdAt: drill.created_at
      }));
    } catch (error) {
      console.error('Error fetching prestige drills:', error);
      return [];
    }
  },

  /**
   * Get count of drills that will be unlocked at next prestige
   * @returns {Promise<number>} Number of drills unlocked at next prestige
   */
  async getNextPrestigeDrillCount() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('prestige_level')
        .eq('id', user.id)
        .single();

      const nextPrestigeLevel = (profile?.prestige_level || 0) + 1;

      const { count, error } = await supabase
        .from('prestige_drills')
        .select('id', { count: 'exact', head: true })
        .eq('prestige_level_required', nextPrestigeLevel)
        .eq('is_active', true);

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error counting next prestige drills:', error);
      return 0;
    }
  },

  /**
   * Get prestige stats summary
   * @returns {Promise<Object>} Stats about prestige system
   */
  async getPrestigeStats() {
    try {
      const { data: { user } } = await supabase.auth.getSession();
      if (!user?.user) throw new Error('Not authenticated');

      const [eligibility, badges, drills, history, nextDrillCount] = await Promise.all([
        this.checkPrestigeEligibility(),
        this.getPrestigeBadges(),
        this.getPrestigeDrills(),
        this.getPrestigeHistory(),
        this.getNextPrestigeDrillCount()
      ]);

      return {
        ...eligibility,
        badges: badges.badges,
        unlockedDrills: drills.length,
        prestigeHistory: history,
        nextPrestigeDrillCount
      };
    } catch (error) {
      console.error('Error fetching prestige stats:', error);
      throw error;
    }
  },

  /**
   * Get prestige leaderboard (top prestigious users)
   * @param {number} limit - Number of top users to return
   * @returns {Promise<Array>} Top prestige users
   */
  async getPrestigeLeaderboard(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, prestige_level, lifetime_xp, prestige_badges')
        .gt('prestige_level', 0)
        .order('prestige_level', { ascending: false })
        .order('lifetime_xp', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((user, index) => ({
        rank: index + 1,
        userId: user.id,
        fullName: user.full_name,
        prestigeLevel: user.prestige_level,
        lifetimeXp: user.lifetime_xp,
        badges: user.prestige_badges || []
      }));
    } catch (error) {
      console.error('Error fetching prestige leaderboard:', error);
      return [];
    }
  }
};

export default prestigeService;
